import { classNames, Divider } from "primereact";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { Toolbar } from "primereact/toolbar";
import React, { useContext, useState } from "react";
import { ContactGroup } from "../../../api/firebase/types/contact.types";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import SaveCancelFooter from "../../../components/footer/SaveCancelFooter";
import ToastContext from "../../../components/toast/ToastContext";
import { useMediaQuery } from "../../../hooks/responsiveLayout";
import ContactGroupStore from "../../../store/ContactGroupStore";
import { ContactGroupToasts } from "../../../toastMessages/contactGroupToastMessages";
import ContactGroupSettingsForm, {
  ContactGroupSettingsFormData,
} from "./ContactGroupSettingsForm";

type ContactGroupSettingsCardProps = {
  currentContactGroup: ContactGroup;
};

const ContactGroupSettingsCard: React.FC<ContactGroupSettingsCardProps> = ({
  currentContactGroup,
}) => {
  const [width] = useMediaQuery();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const showToast = useContext(ToastContext);

  const onCancelContactGroupSettings = () => {
    ContactGroupStore.setCurrentContactGroup(undefined);
  };

  const updateCurrentContactGroup = (
    contactGroupData: ContactGroupSettingsFormData
  ) => {
    setShowLoader(true);
    ContactGroupStore.updateContactGroup(contactGroupData)
      .then(() => {
        showToast(ContactGroupToasts.contactGroupContactsSaved);
      })
      .catch(() => {
        showToast(ContactGroupToasts.contactGroupContactsSaveError);
      })
      .finally(() => setShowLoader(false));
  };

  const header = (
    <Button
      key="delete-button"
      icon="pi pi-trash"
      className="p-button-outlined p-button-danger"
      style={{ height: 40 }}
      label="Entfernen"
      onClick={() => setShowDeleteDialog(true)}
    />
  );

  const handleDeleteContactGroupClicked = async () => {
    setShowLoader(true);
    const { contactGroupDeleted, contactGroupDeletionError } =
      ContactGroupToasts;
    const deletionSucceeded =
      await ContactGroupStore.deleteCurrentContactGroup();
    showToast(
      deletionSucceeded ? contactGroupDeleted : contactGroupDeletionError
    );
    setShowLoader(false);
  };

  return (
    <Card
      className={classNames("p-col", {
        "p-m-2": width < 580,
      })}
      footer={
        <SaveCancelFooter
          formId="contactGroupSettings"
          cancelAction={onCancelContactGroupSettings}
        />
      }
    >
      {showLoader && <ProgressBar mode="indeterminate" />}
      <Toolbar
        left={() => <h2>{currentContactGroup.label}</h2>}
        right={() => header}
      />
      <Divider />
      <ContactGroupSettingsForm
        currentContactGroup={currentContactGroup}
        onSave={updateCurrentContactGroup}
      />
      <ConfirmDialog
        header="Kontaktgruppe entfernen?"
        content="Willst du die Kontaktgruppe wirklich entfernen?"
        isVisible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onClickYes={handleDeleteContactGroupClicked}
      />
    </Card>
  );
};

export default observer(ContactGroupSettingsCard);
